import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  finalize,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { GENDER_OPTIONS, PERMITTED_VIEWS } from 'src/app/core/helpers/global/global.constant';
import { getDifferingPropertiesArray, isChangedAndInvalid } from 'src/app/core/helpers/global/global.util';
import {
  AGENT_FORM_SECTION_CONTROLS,
  AgentDetailTab,
  TYPES_USER_PERMISSIONS,
  USER_AGENT,
  USER_VALIDATIONS,
} from 'src/app/core/helpers/global/user-management.constant';
import {
  BUTTON_ACTIONS,
  FORMAT_FOR_DATES,
  NG_SELECT_QUERIES,
} from 'src/app/core/helpers/ui/ui.constant';
import {
  ReferredUser,
  TransactionCommission,
  User,
} from 'src/app/core/interfaces/api/user.interface';
import { ModalWithAction } from 'src/app/core/interfaces/ui/bootstrap-modal.interface';
import { ButtonAction, NgSelect, NgSelectQuery } from 'src/app/core/interfaces/ui/ui.interface';
import { CityService } from 'src/app/core/services/api/city.service';
import { CommissionPlanService } from 'src/app/core/services/api/commission-plan.service';
import { CountryService } from 'src/app/core/services/api/country.service';
import { CurrencyService } from 'src/app/core/services/api/currency.service';
import { LanguageService } from 'src/app/core/services/api/language.service';
import { RoleService } from 'src/app/core/services/api/role.service';
import { GlobalService } from 'src/app/core/services/ui/global.service';
import { UserService } from 'src/app/core/services/api/user.service';
import { BootstrapModalService } from 'src/app/core/services/ui/bootstrap-modal.service';
import { FilterService } from 'src/app/core/services/ui/filter.service';
import { SearchNgSelectService } from 'src/app/core/services/ui/search-ng-select.service';
import { ToastrNotificationService } from 'src/app/core/services/ui/toastr-notification.service';
import { strongPasswordValidator } from 'src/app/shared/validators/passwordMatch';
import { adultValidator } from 'src/app/shared/validators/validatorsAdult';
import { phoneNumberValidator } from 'src/app/shared/validators/validatorsPhoneNumber';

const SENSITIVE_CONTROLS = [
  'email',
  'mobileNumber',
  'conventionalNumber',
  'whatsappNumber',
  'documentNumber',
  'birthDate',
  'address',
] as const;

@Component({
  selector: 'user-management-agent-create-modal',
  templateUrl: './agent-create-modal.component.html',
  providers: [
    { provide: 'ngCurrencies', useClass: SearchNgSelectService },
    { provide: 'ngCountries', useClass: SearchNgSelectService },
    { provide: 'ngCities', useClass: SearchNgSelectService },
    { provide: 'ngBirthCountries', useClass: SearchNgSelectService },
    { provide: 'ngBirthCities', useClass: SearchNgSelectService },
    { provide: 'ngLanguages', useClass: SearchNgSelectService },
    { provide: 'ngUsers', useClass: SearchNgSelectService },
    { provide: 'ngCommissionPlans', useClass: SearchNgSelectService },
    { provide: 'ngRoles', useClass: SearchNgSelectService },
    { provide: 'ngReferalPercentages', useClass: SearchNgSelectService },
    { provide: 'ngCountriesWithPhoneCodes', useClass: SearchNgSelectService },
  ],
})
export class AgentCreateModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() inline: boolean = false;
  @Input() readonly: boolean = false;
  @Input() agent: User | null = null;
  @Input() section: AgentDetailTab | null = null;
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() invalidSection = new EventEmitter<AgentDetailTab>();

  public AgentDetailTab = AgentDetailTab;
  public BUTTON_ACTIONS = BUTTON_ACTIONS;
  public NG_SELECT_QUERIES = NG_SELECT_QUERIES;
  public USER_VALIDATIONS = USER_VALIDATIONS;
  public FORMAT_FOR_DATES = FORMAT_FOR_DATES;

  public countries$: Observable<NgSelect<string>[]> = of([]);
  public cities$: Observable<NgSelect<string>[]> = of([]);
  public birthCountries$: Observable<NgSelect<string>[]> = of([]);
  public birthCities$: Observable<NgSelect<string>[]> = of([]);
  public currencies$: Observable<NgSelect<string>[]> = of([]);
  public languages$: Observable<NgSelect<string>[]> = of([]);
  public genders$: Observable<NgSelect<string>[]> = of(GENDER_OPTIONS);
  public users$: Observable<NgSelect<string>[]> = of([]);
  public commissionPlans$: Observable<NgSelect<string>[]> = of([]);
  public roles$: Observable<NgSelect<string>[]> = of([]);
  public referalPercentages$: Observable<NgSelect<string>[]> = of([]);
  public countriesWithPhoneCodes$: Observable<NgSelect<string>[]> = of([]);

  public titleModal: string = '';
  public createdAt: Date | undefined = undefined;
  public updatedAt: Date | undefined = undefined;
  public agentModalForm: FormGroup | undefined = undefined;
  public activeButtonAction: ButtonAction | undefined = undefined;
  public betLimit: User | undefined = undefined;
  public showPassword: boolean = false;
  public isPhysicalCashRole: boolean = false;
  public canViewSensitiveData: boolean = false;
  private isAdminViewer: boolean = false;
  public isSubmitting: boolean = false;

  private readonly physicalCashRequiredFields = [
    'localName',
    'localAddress',
    'localMapLink',
    'localOpeningTime',
    'localClosingTime',
  ];

  private selectedUser: User | undefined = undefined;
  private originalFormValue: Record<string, any> | undefined = undefined;
  private unsubscribe$: Subject<boolean> = new Subject<boolean>();
  private formReset$: Subject<void> = new Subject<void>();

  constructor(
    private _formBuilder: FormBuilder,
    private _currencyService: CurrencyService,
    private _countryService: CountryService,
    private _cityService: CityService,
    private _languageService: LanguageService,
    private _roleService: RoleService,
    private _globalService: GlobalService,
    private _commissionService: CommissionPlanService,
    private _bsModalService: BootstrapModalService<ModalWithAction<User>>,
    private _userService: UserService,
    private _filterService: FilterService<object>,
    private _translateService: TranslateService,
    private _notificationService: ToastrNotificationService,

    @Inject('ngCurrencies')
    private _ngCurrencies: SearchNgSelectService<string>,
    @Inject('ngCountries')
    private _ngCountries: SearchNgSelectService<string>,
    @Inject('ngCities')
    private _ngCities: SearchNgSelectService<string>,
    @Inject('ngBirthCountries')
    private _ngBirthCountries: SearchNgSelectService<string>,
    @Inject('ngBirthCities')
    private _ngBirthCities: SearchNgSelectService<string>,
    @Inject('ngLanguages')
    private _ngLanguages: SearchNgSelectService<string>,
    @Inject('ngUsers')
    private _ngUsers: SearchNgSelectService<string>,
    @Inject('ngCommissionPlans')
    private _ngCommissionPlans: SearchNgSelectService<string>,
    @Inject('ngRoles')
    private _ngRoles: SearchNgSelectService<string>,
    @Inject('ngReferalPercentages')
    private _ngReferalPercentages: SearchNgSelectService<string>,
    @Inject('ngCountriesWithPhoneCodes')
    private _ngCountriesWithPhoneCodes: SearchNgSelectService<string>
  ) {}

  ngOnInit(): void {
    this._globalService.profile.pipe(takeUntil(this.unsubscribe$)).subscribe((profile) => {
      const userPermissions = this._roleService.getIdsWithTrueStatus(
        profile?.roleId?.permissions ?? []
      );
      this.canViewSensitiveData = userPermissions.includes(
        PERMITTED_VIEWS.VIEW_AGENT_SENSITIVE_DATA
      );
      this.isAdminViewer = profile?.userType === TYPES_USER_PERMISSIONS.ADMIN;
      this.disableSensitiveControls();
      this.lockTwoFactorActivation();
    });

    this.setConfigNgSelects();
    if (this.inline) {
      this.processInlineAgent();
    } else {
      this.suscribeModalDataIssued();
    }
    if (this.activeButtonAction === BUTTON_ACTIONS.ADD) {
      for (let i = 0; i < 1; i++) {
        this.addReferred();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const modeChanged = changes['agent'] || changes['readonly'];
    if (this.inline && modeChanged && !modeChanged.firstChange) {
      this.processInlineAgent();
    }
  }

  public get isSensitiveLocked(): boolean {
    return !this.canViewSensitiveData && this.activeButtonAction !== BUTTON_ACTIONS.ADD;
  }

  public isSectionVisible(section: AgentDetailTab): boolean {
    return !this.inline || this.section === section;
  }

  public cancelEdit(): void {
    this.cancelled.emit();
  }

  private processInlineAgent(): void {
    if (!this.agent) return;
    this.processSelectedUser(this.readonly ? BUTTON_ACTIONS.VIEW : BUTTON_ACTIONS.EDIT, this.agent);
  }

  private setConfigNgSelects(): void {
    this._ngCurrencies.setSearchTermKey('name');
    this._ngCurrencies.setFetchDataFunction(
      this._currencyService.findCurrenciesForSelect.bind(this._currencyService)
    );
    this.currencies$ = this._ngCurrencies.getData();

    this._ngCountries.setSearchTermKey('name');
    this._ngCountries.setFetchDataFunction(
      this._countryService.findCountriesForSelect.bind(this._countryService)
    );
    this.countries$ = this._ngCountries.getData();

    this._ngCountriesWithPhoneCodes.setSearchTermKey('searchTerm');
    this._ngCountriesWithPhoneCodes.setFetchDataFunction(
      this._countryService.findTelephoneCodesForSelect.bind(this._countryService)
    );
    this.countriesWithPhoneCodes$ = this._ngCountriesWithPhoneCodes.getData();

    this._ngCities.setSearchTermKey('name');
    this._ngCities.setFetchDataFunction(
      this._cityService.findCitiesForSelect.bind(this._cityService)
    );
    this.cities$ = this._ngCities.getData();

    this._ngBirthCountries.setSearchTermKey('name');
    this._ngBirthCountries.setFetchDataFunction(
      this._countryService.findCountriesForSelect.bind(this._countryService)
    );
    this.birthCountries$ = this._ngBirthCountries.getData();

    this._ngBirthCities.setSearchTermKey('name');
    this._ngBirthCities.setFetchDataFunction(
      this._cityService.findCitiesForSelect.bind(this._cityService)
    );
    this.birthCities$ = this._ngBirthCities.getData();

    this._ngReferalPercentages.setSearchTermKey('percentage');
    this._ngReferalPercentages.setFetchDataFunction(
      this._commissionService.getAllReferalPercentageBySelect.bind(this._commissionService)
    );
    this.referalPercentages$ = this._ngReferalPercentages.getData();

    this._ngLanguages.setSearchTermKey('name');
    this._ngLanguages.setFetchDataFunction(
      this._languageService.findLanguagesForSelect.bind(this._languageService)
    );
    this.languages$ = this._ngLanguages.getData();

    this._ngUsers.setSearchTermKey('username');
    this._ngUsers.setFetchDataFunction(
      this._userService.findUsersForSelect.bind(this._userService)
    );
    this.users$ = this._ngUsers.getData();

    this._ngCommissionPlans.setSearchTermKey('planName');
    this._ngCommissionPlans.setFetchDataFunction(
      this._commissionService.findCommissionPlansForSelect.bind(this._commissionService)
    );
    this.commissionPlans$ = this._ngCommissionPlans.getData();

    this._ngRoles.setSearchTermKey('name');

    this._ngRoles.extendFilter({
      excludeUserTypes: [
        TYPES_USER_PERMISSIONS.ADMIN,
        TYPES_USER_PERMISSIONS.COLLABORATOR_AGENT,
        TYPES_USER_PERMISSIONS.COLLABORATOR_OPERATOR,
      ],
    });

    this._ngRoles.setFetchDataFunction(this._roleService.findRoleForSelect.bind(this._roleService));

    this.roles$ = this._ngRoles.getData();
    this._ngRoles.triggerFetchData();
  }

  private suscribeModalDataIssued(): void {
    this._bsModalService
      .getDataIssued()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ buttonAction, selectedRow }) =>
          this.processSelectedUser(buttonAction, selectedRow),
        error: () => this.closeModal(),
      });
  }

  private processSelectedUser(buttonAction: ButtonAction, user?: User): void {
    this.formReset$.next();
    this.activeButtonAction = buttonAction;
    this.selectedUser = user;
    this.betLimit = user;
    this.agentModalForm = this.getConfigModalForm(this.activeButtonAction);
    this.subscribeToTransactionCommissionToggle();
    this.populateModalForm(this.selectedUser);
    this.subscribeToWhatsappMirror();

    const executeAction = this.handleProcessButtonAction(buttonAction);
    executeAction();
    this.disableSensitiveControls();
    this.lockTwoFactorActivation();
    this.originalFormValue =
      buttonAction === BUTTON_ACTIONS.EDIT ? this.agentModalForm.getRawValue() : undefined;
  }

  private lockTwoFactorActivation(): void {
    if (this.activeButtonAction !== BUTTON_ACTIONS.EDIT) return;
    const control = this.agentModalForm?.get('TFA');
    if (!control) return;
    const canSwitchOff = this.isAdminViewer && this.selectedUser?.TFA === true;
    if (canSwitchOff) control.enable({ emitEvent: false });
    else control.disable({ emitEvent: false });
  }

  public hasBlockingErrors(): boolean {
    const form = this.agentModalForm;
    if (!form) return false;
    if (!this.originalFormValue) return form.invalid;
    return isChangedAndInvalid(form, this.originalFormValue);
  }

  private disableSensitiveControls(): void {
    if (this.canViewSensitiveData || this.activeButtonAction !== BUTTON_ACTIONS.EDIT) return;
    SENSITIVE_CONTROLS.forEach((name) => this.agentModalForm?.get(name)?.disable());
  }

  private getConfigModalForm(buttonAction: ButtonAction): FormGroup {
    const defaultConfig = {
      firstName: [null, [Validators.required]],
      lastName: [null, [Validators.required]],
      documentNumber: [null, [Validators.required]],
      countryId: [null, [Validators.required]],
      cityId: [null, [Validators.required]],
      birthDate: [null, [Validators.required, adultValidator()]],
      address: [null],
      userType: [null],
      gender: [null, [Validators.required]],
      email: [null, [Validators.required, Validators.email]],
      telephoneCodeId: [null, [Validators.required]],
      mobileNumber: [null, [Validators.required, phoneNumberValidator()]],
      conventionalNumber: [null],
      whatsappCodeId: [null],
      whatsappNumber: [null],
      username: ['', [Validators.required, Validators.pattern(/^[A-Za-z\d_-]{4,}$/)]],
      languageId: [null, [Validators.required]],
      currencyId: [null],
      comissionPlanId: [null, [Validators.required]],
      roleId: [null, [Validators.required]],
      referred: this._formBuilder.array([]),
      birthCountryId: [null, [Validators.required]],
      birthCityId: [null, [Validators.required]],
      localName: [null],
      localAddress: [null],
      localMapLink: [null],
      localPhone: [null],
      localWhatsappCodeId: [null],
      localWhatsappNumber: [null],
      localOpeningTime: [null],
      localClosingTime: [null],
      transactionCommission: this._formBuilder.group({
        enabled: [false],
        depositRate: [{ value: 0, disabled: true }, [Validators.min(0), Validators.max(100)]],
        withdrawalRate: [{ value: 0, disabled: true }, [Validators.min(0), Validators.max(100)]],
      }),
    };

    const addUserConfig = {
      ...defaultConfig,
      password: ['', [Validators.required, strongPasswordValidator()]],
    };

    const viewAndEditUserConfig = {
      ...defaultConfig,
      isLocked: [false],
      state: [false],
      TFA: [false],
    };

    const actionsMap = new Map<ButtonAction, object>([
      [BUTTON_ACTIONS.ADD, addUserConfig],
      [BUTTON_ACTIONS.EDIT, viewAndEditUserConfig],
      [BUTTON_ACTIONS.VIEW, viewAndEditUserConfig],
    ]);

    const config = actionsMap.get(buttonAction) || {};

    const formGroup = this._formBuilder.group(config);
    formGroup.setValidators([
      this.pairDependencyValidator('whatsappCodeId', 'whatsappNumber', 'whatsappDependency'),
      this.pairDependencyValidator(
        'localWhatsappCodeId',
        'localWhatsappNumber',
        'localWhatsappDependency'
      ),
    ]);
    formGroup.updateValueAndValidity();
    return formGroup;
  }
  private pairDependencyValidator(
    codeField: string,
    numberField: string,
    errorKey: string
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const form = control as FormGroup;
      const codeValue = form.get(codeField)?.value;
      const numberValue = form.get(numberField)?.value;

      const hasCode = codeValue !== null && codeValue !== undefined && codeValue !== '';
      const hasNumber = numberValue !== null && numberValue !== undefined && numberValue !== '';

      if ((hasCode && !hasNumber) || (!hasCode && hasNumber)) {
        return { [errorKey]: true };
      }

      return null;
    };
  }

  private populateModalForm(user?: User): void {
    if (!user || !this.agentModalForm) return;

    const {
      firstName,
      lastName,
      documentNumber,
      birthDate,
      username,
      email,
      mobileNumber,
      telephoneCodeId,
      conventionalNumber,
      whatsappCodeId,
      whatsappNumber,
      countryId,
      cityId,
      birthCountryId,
      birthCityId,
      address,
      gender,
      currencyId,
      languageId,
      comissionPlanId,
      state,
      isLocked,
      TFA,
      createdAt,
      updatedAt,
      referred,
      roleId,
      userType,
      isPhysicalCash,
      localName,
      localAddress,
      localMapLink,
      localPhone,
      localWhatsappCodeId,
      localWhatsappNumber,
      localOpeningTime,
      localClosingTime,
      transactionCommission,
    } = user;

    const selectedUser = {
      firstName,
      lastName,
      birthDate: birthDate ? birthDate.toString().split('T')[0] : null,
      documentNumber,
      username,
      email,
      mobileNumber,
      telephoneCodeId: telephoneCodeId?._id,
      conventionalNumber,
      whatsappCodeId: whatsappCodeId?._id,
      whatsappNumber,
      countryId: countryId?._id,
      currencyId: currencyId?._id,
      cityId: cityId?._id,
      birthCountryId: birthCountryId?._id,
      birthCityId: birthCityId?._id,
      address,
      gender,
      languageId: languageId?._id,
      comissionPlanId: comissionPlanId?._id,
      state: state === 1 ? true : false,
      isLocked,
      TFA,
      roleId: roleId?._id,
      userType,
      localName,
      localAddress,
      localMapLink,
      localPhone,
      localWhatsappCodeId: localWhatsappCodeId?._id,
      localWhatsappNumber,
      localOpeningTime,
      localClosingTime,
      transactionCommission: this.normalizeTransactionCommission(transactionCommission),
    };
    const referredFormArray = this.agentModalForm.get('referred') as FormArray;
    referredFormArray.clear();
    if (referred && referred.length > 0) {
      referred.forEach((ref: ReferredUser) => {
        const referredGroup = this._formBuilder.group({
          referralId: [ref.referralId?._id || null],
          percentage: [
            this.toPercentageOptionValue(ref.percentage),
            [Validators.min(0.5), Validators.max(100)],
          ],
        });
        this.linkPercentageToReferral(referredGroup);
        referredFormArray.push(referredGroup);
      });
    }
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.agentModalForm.patchValue(selectedUser);
    this.setPhysicalCashRole(!!(isPhysicalCash ?? roleId?.isPhysicalCash));
    if (roleId?.name) {
      this._roleService
        .findRole({ name: roleId.name })
        .pipe(takeUntil(merge(this.unsubscribe$, this.formReset$)))
        .subscribe({
          next: (res) => {
            const matchingRole = res.data.result?.find((role) => role._id === roleId._id);
            if (matchingRole) this.setPhysicalCashRole(!!matchingRole.isPhysicalCash);
          },
        });
    }
    if (localWhatsappNumber) {
      this.agentModalForm.get('localWhatsappCodeId')?.markAsDirty();
      this.agentModalForm.get('localWhatsappNumber')?.markAsDirty();
    }
  }

  private handleProcessButtonAction(buttonAction: ButtonAction): () => void {
    const actionMap = new Map<ButtonAction, () => void>([
      [BUTTON_ACTIONS.ADD, () => this.handleAddUser()],
      [BUTTON_ACTIONS.EDIT, () => this.handleEditUser()],
      [BUTTON_ACTIONS.VIEW, () => this.handleViewUser()],
    ]);
    return actionMap.get(buttonAction) || (() => {});
  }

  private handleAddUser(): void {
    this.titleModal = 'userManagement.users.add';
    this._ngUsers.extendFilter({ type: USER_AGENT });
    this._ngCurrencies.triggerFetchData();
    this._ngCountries.triggerFetchData();
    this._ngBirthCountries.triggerFetchData();
    this._ngLanguages.triggerFetchData();
    this._ngUsers.triggerFetchData();
    this._ngCommissionPlans.triggerFetchData();
    this._ngRoles.triggerFetchData();
    this._ngReferalPercentages.triggerFetchData();
    this._ngCountriesWithPhoneCodes.triggerFetchData();
  }

  private handleEditUser(): void {
    this.titleModal = 'userManagement.users.edit';

    if (!this.agentModalForm || !this.selectedUser) return;

    this.handleAddDataToStream(this.selectedUser);

    const { countryId } = this.selectedUser;
    this._ngCities.extendFilter({ countryId: countryId._id });
    this._ngBirthCities.extendFilter({ countryId: countryId._id });
    this._ngUsers.extendFilter({ type: USER_AGENT });

    this._ngCurrencies.triggerFetchData();
    this._ngCountries.triggerFetchData();
    this._ngBirthCountries.triggerFetchData();
    this._ngCities.triggerFetchData();
    this._ngLanguages.triggerFetchData();
    this._ngUsers.triggerFetchData();
    this._ngCommissionPlans.triggerFetchData();
    this._ngRoles.triggerFetchData();
    this._ngReferalPercentages.triggerFetchData();
    this._ngCountriesWithPhoneCodes.triggerFetchData();
  }

  private handleViewUser(): void {
    this.titleModal = 'userManagement.users.view';

    if (!this.agentModalForm || !this.selectedUser) return;

    this.handleAddDataToStream(this.selectedUser);
    this.agentModalForm.disable();
  }

  private handleAddDataToStream(selectedUser: User): void {
    const {
      countryId,
      cityId,
      languageId,
      referred,
      comissionPlanId,
      roleId,
      birthCityId,
      birthCountryId,
    } = selectedUser;
    const country = {
      label: countryId?.name,
      value: countryId?._id,
      icon: countryId?.code,
    };
    const city = { label: cityId?.name, value: cityId?._id };
    const language = { label: languageId?.name, value: languageId?._id };
    const commissionPlan = {
      label: comissionPlanId?.planName,
      value: comissionPlanId?._id,
    };
    const role = {
      label: roleId?.name,
      value: roleId?._id,
      isPhysicalCash: roleId?.isPhysicalCash,
    };

    const birthCountry = {
      label: birthCountryId?.name,
      value: birthCountryId?._id,
      icon: birthCountryId?.code,
    };
    const birthCity = { label: birthCityId?.name, value: birthCityId?._id };
    const currency = {
      label: selectedUser.currencyId?.name,
      value: selectedUser.currencyId?._id,
    };

    const countriesWithPhoneCodes = {
      label: `${selectedUser.telephoneCodeId?.name} (${selectedUser.telephoneCodeId?.telephoneCode})`,
      value: selectedUser.telephoneCodeId?._id,
    };

    const percentages =
      referred?.map((ref: ReferredUser) => ({
        label: `${ref.percentage}%`,
        value: `${ref.percentage}`,
      })) || [];
    this._ngReferalPercentages.addDataToStream(percentages);

    this._ngCountries.addDataToStream([country]);
    const phoneCodesToStream = [countriesWithPhoneCodes];
    if (
      selectedUser.whatsappCodeId?._id &&
      selectedUser.whatsappCodeId?._id !== selectedUser.telephoneCodeId?._id
    ) {
      const whatsappCountriesWithPhoneCodes = {
        label: `${selectedUser.whatsappCodeId.name} (${selectedUser.whatsappCodeId.telephoneCode})`,
        value: selectedUser.whatsappCodeId._id,
      };
      phoneCodesToStream.push(whatsappCountriesWithPhoneCodes);
    }
    if (
      selectedUser.localWhatsappCodeId?._id &&
      selectedUser.localWhatsappCodeId?._id !== selectedUser.telephoneCodeId?._id
    ) {
      phoneCodesToStream.push({
        label: `${selectedUser.localWhatsappCodeId.name} (${selectedUser.localWhatsappCodeId.telephoneCode})`,
        value: selectedUser.localWhatsappCodeId._id,
      });
    }
    this._ngCountriesWithPhoneCodes.addDataToStream(phoneCodesToStream);
    this._ngCurrencies.addDataToStream([currency]);
    this._ngBirthCountries.addDataToStream([birthCountry]);
    this._ngCities.addDataToStream([city]);
    this._ngBirthCities.addDataToStream([birthCity]);
    this._ngLanguages.addDataToStream([language]);
    this._ngCommissionPlans.addDataToStream([commissionPlan]);
    this._ngRoles.addDataToStream([role]);
    const users =
      referred?.map((ref: ReferredUser) => ({
        label: ref.referralId.username || 'N/A',
        value: `${ref.referralId._id}` || '',
      })) || [];
    this._ngUsers.addDataToStream(users);
  }

  public onSearchSelect(typeQuery: NgSelectQuery, term?: string): void {
    const termText = term || '';

    const actionMap = new Map<NgSelectQuery, () => void>([
      [NG_SELECT_QUERIES.COUNTRIES, () => this._ngCountries.searchTerm(termText)],
      [NG_SELECT_QUERIES.CITIES, () => this.handleSearchTermForCities(termText)],
      [NG_SELECT_QUERIES.BIRTH_COUNTRIES, () => this._ngBirthCountries.searchTerm(termText)],
      [NG_SELECT_QUERIES.BIRTH_CITIES, () => this.handleSearchTermForBirthCities(termText)],
      [NG_SELECT_QUERIES.CURRENCIES, () => this._ngCurrencies.searchTerm(termText)],
      [NG_SELECT_QUERIES.LANGUAGE, () => this._ngLanguages.searchTerm(termText)],
      [NG_SELECT_QUERIES.USERS, () => this._ngUsers.searchTerm(termText)],
      [NG_SELECT_QUERIES.COMISSION_PLANS, () => this._ngCommissionPlans.searchTerm(termText)],
      [NG_SELECT_QUERIES.USER_ROLES, () => this._ngRoles.searchTerm(termText)],
      [NG_SELECT_QUERIES.PERCENTAGES, () => this._ngReferalPercentages.searchTerm(termText)],
      [
        NG_SELECT_QUERIES.TELEPHONE_CODES,
        () => this._ngCountriesWithPhoneCodes.searchTerm(termText),
      ],
    ]);

    const action = actionMap.get(typeQuery);
    if (action) action();
  }

  private handleSearchTermForCities(term: string): void {
    const countryId = this.agentModalForm?.value.countryId;
    if (countryId) {
      this._ngCities.extendFilter({ countryId });
      this._ngCities.searchTerm(term);
    }
  }

  private handleSearchTermForBirthCities(term: string): void {
    const birthCountryId = this.agentModalForm?.value.birthCountryId;
    if (birthCountryId) {
      this._ngBirthCities.extendFilter({ countryId: birthCountryId });
      this._ngBirthCities.searchTerm(term);
    }
  }

  public onScrollToEndSelect(typeQuery: NgSelectQuery): void {
    const actionMap = new Map<NgSelectQuery, () => void>([
      [NG_SELECT_QUERIES.COUNTRIES, () => this._ngCountries.scrollToEnd()],
      [NG_SELECT_QUERIES.CITIES, () => this._ngCities.scrollToEnd()],
      [NG_SELECT_QUERIES.BIRTH_COUNTRIES, () => this._ngBirthCountries.scrollToEnd()],
      [NG_SELECT_QUERIES.BIRTH_CITIES, () => this._ngBirthCities.scrollToEnd()],
      [NG_SELECT_QUERIES.CURRENCIES, () => this._ngCurrencies.scrollToEnd()],
      [NG_SELECT_QUERIES.LANGUAGE, () => this._ngLanguages.scrollToEnd()],
      [NG_SELECT_QUERIES.USERS, () => this._ngUsers.scrollToEnd()],
      [NG_SELECT_QUERIES.COMISSION_PLANS, () => this._ngCommissionPlans.scrollToEnd()],
      [NG_SELECT_QUERIES.USER_ROLES, () => this._ngRoles.scrollToEnd()],
      [NG_SELECT_QUERIES.PERCENTAGES, () => this._ngReferalPercentages.scrollToEnd()],
      [NG_SELECT_QUERIES.TELEPHONE_CODES, () => this._ngCountriesWithPhoneCodes.scrollToEnd()],
    ]);

    const action = actionMap.get(typeQuery);
    if (action) action();
  }

  public onCountryChange(event: NgSelect<string>): void {
    if (!this.agentModalForm || !event) return;

    this.agentModalForm.patchValue({
      cityId: '',
    });

    this._ngCities.resetNgSelect();
    this._ngCities.extendFilter({ countryId: event.value });
    this._ngCities.triggerFetchData();
  }

  public onBirthCountryChange(event: NgSelect<string>): void {
    if (!this.agentModalForm || !event) return;

    this.agentModalForm.patchValue({
      birthCityId: '',
    });

    this._ngBirthCities.resetNgSelect();
    this._ngBirthCities.extendFilter({ countryId: event.value });
    this._ngBirthCities.triggerFetchData();
  }

  public closeModal(id?: string): void {
    this._bsModalService.closeModal(id);
  }

  public onSubmit(): void {
    if (this.isSubmitting || !this.activeButtonAction || !this.agentModalForm) return;
    if (this.hasBlockingErrors()) {
      if (this.inline) this.flagInvalidFields();
      return;
    }
    this.cleanEmptyReferred();
    const executeAction = this.handleSubmitAction(this.activeButtonAction);
    executeAction();
  }

  private flagInvalidFields(): void {
    const form = this.agentModalForm;
    if (!form) return;

    const isInvalid = (name: string) =>
      this.originalFormValue
        ? isChangedAndInvalid(form.get(name), this.originalFormValue[name])
        : !!form.get(name)?.invalid;

    if (this.originalFormValue) {
      Object.keys(form.controls)
        .filter(isInvalid)
        .forEach((name) => form.get(name)?.markAllAsTouched());
    } else {
      form.markAllAsTouched();
    }

    const invalidTabs = Object.values(AgentDetailTab).filter((tab) => {
      const controls = AGENT_FORM_SECTION_CONTROLS[tab];
      const hasInvalidControl = controls.some(isInvalid);
      const hasWhatsappError =
        (tab === AgentDetailTab.CONTACT && form.hasError('whatsappDependency')) ||
        (tab === AgentDetailTab.AGENCY && form.hasError('localWhatsappDependency'));
      return hasInvalidControl || hasWhatsappError;
    });

    if (invalidTabs.length && !(this.section && invalidTabs.includes(this.section))) {
      this.invalidSection.emit(invalidTabs[0]);
    }
  }

  private onUpdateSuccess(): void {
    if (this.inline) {
      this.saved.emit();
      return;
    }
    this.closeModal(this.activeButtonAction);
  }

  private handleSubmitAction(buttonAction: ButtonAction): () => void {
    const actionMap = new Map<ButtonAction, () => void>([
      [BUTTON_ACTIONS.ADD, () => this.addUser()],
      [BUTTON_ACTIONS.EDIT, () => this.editUser()],
    ]);
    return actionMap.get(buttonAction) || (() => {});
  }

  private addUser(): void {
    const {
      firstName,
      lastName,
      birthDate,
      documentNumber,
      username,
      email,
      password,
      mobileNumber,
      telephoneCodeId,
      conventionalNumber,
      whatsappCodeId,
      whatsappNumber,
      countryId,
      cityId,
      birthCountryId,
      birthCityId,
      address,
      gender,
      languageId,
      comissionPlanId,
      currencyId,
      roleId,
      referred,
      userType,
      localName,
      localAddress,
      localMapLink,
      localPhone,
      localWhatsappCodeId,
      localWhatsappNumber,
      localOpeningTime,
      localClosingTime,
      transactionCommission,
    } = this.agentModalForm!.value;

    const userFormData = {
      firstName,
      lastName,
      birthDate,
      documentNumber,
      username,
      email,
      password,
      mobileNumber,
      telephoneCodeId,
      conventionalNumber,
      whatsappCodeId,
      whatsappNumber,
      countryId,
      cityId,
      birthCountryId,
      birthCityId,
      address,
      gender,
      languageId,
      comissionPlanId,
      currencyId,
      roleId,
      referred,
      userType,
      localName,
      localAddress,
      localMapLink,
      localPhone,
      localWhatsappCodeId,
      localWhatsappNumber,
      localOpeningTime,
      localClosingTime,
      transactionCommission: this.normalizeTransactionCommission(transactionCommission),
    };

    this.isSubmitting = true;
    this._userService
      .createUser(userFormData)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({ next: () => this.closeModal(this.activeButtonAction) });
  }

  private editUser(): void {
    if (!this.agentModalForm || !this.selectedUser) return;

    const selectedUser = this.getSelectedUser();
    const userFormData = this.getUserFormData();

    const editedUser = getDifferingPropertiesArray(selectedUser, userFormData);
    const hasProfileChanges = Object.keys(editedUser).length > 0;
    const disableTwoFactor =
      this.selectedUser.TFA === true && this.agentModalForm.get('TFA')?.value === false;

    if (!hasProfileChanges && !disableTwoFactor) {
      const message = this._translateService.instant('words.noChangesForm');
      this._notificationService.showNotification({
        type: 'warning',
        title: 'userManagement.users.title',
        message,
      });
      return;
    }

    const { _id } = this.selectedUser;
    const requests: Observable<unknown>[] = [];
    if (hasProfileChanges) requests.push(this._userService.updateUser(_id, editedUser));
    if (disableTwoFactor) requests.push(this._userService.disableTwoFactor(_id));

    this.isSubmitting = true;
    forkJoin(requests)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({ next: () => this.onUpdateSuccess() });
  }

  private getSelectedUser(): object {
    const {
      firstName,
      lastName,
      birthDate,
      documentNumber,
      username,
      email,
      mobileNumber,
      telephoneCodeId,
      conventionalNumber,
      whatsappCodeId,
      whatsappNumber,
      countryId,
      cityId,
      birthCountryId,
      birthCityId,
      address,
      gender,
      currencyId,
      languageId,
      comissionPlanId,
      state,
      isLocked,
      referred,
      roleId,
      userType,
      localName,
      localAddress,
      localMapLink,
      localPhone,
      localWhatsappCodeId,
      localWhatsappNumber,
      localOpeningTime,
      localClosingTime,
      transactionCommission,
    } = this.selectedUser!;

    const normalizedReferred = referred?.map((ref: ReferredUser) => ({
      referralId: ref.referralId?._id || ref.referralId,
      percentage: this.toPercentageOptionValue(ref.percentage),
    }));
    return {
      firstName,
      lastName,
      birthDate: birthDate ? birthDate.toString().split('T')[0] : null,
      documentNumber,
      username,
      email,
      mobileNumber,
      telephoneCodeId: telephoneCodeId?._id,
      conventionalNumber,
      whatsappCodeId: whatsappCodeId?._id,
      whatsappNumber,
      currencyId: currencyId?._id,
      countryId: countryId?._id,
      cityId: cityId?._id,
      birthCountryId: birthCountryId?._id,
      birthCityId: birthCityId?._id,
      address,
      gender,
      languageId: languageId?._id,
      comissionPlanId: comissionPlanId?._id,
      state: state === 1 ? true : false,
      isLocked,
      referred: normalizedReferred,
      roleId: roleId?._id,
      userType,
      localName,
      localAddress,
      localMapLink,
      localPhone,
      localWhatsappCodeId: localWhatsappCodeId?._id,
      localWhatsappNumber,
      localOpeningTime,
      localClosingTime,
      transactionCommission: this.normalizeTransactionCommission(transactionCommission),
    };
  }

  private getUserFormData(): object {
    const {
      firstName,
      lastName,
      birthDate,
      documentNumber,
      username,
      email,
      mobileNumber,
      telephoneCodeId,
      conventionalNumber,
      whatsappCodeId,
      whatsappNumber,
      countryId,
      cityId,
      birthCountryId,
      birthCityId,
      address,
      gender,
      languageId,
      comissionPlanId,
      currencyId,
      state,
      isLocked,
      referred,
      roleId,
      userType,
      localName,
      localAddress,
      localMapLink,
      localPhone,
      localWhatsappCodeId,
      localWhatsappNumber,
      localOpeningTime,
      localClosingTime,
      transactionCommission,
    } = this.agentModalForm!.value;
    return {
      firstName,
      lastName,
      birthDate,
      documentNumber,
      username,
      email,
      mobileNumber,
      telephoneCodeId,
      conventionalNumber,
      whatsappCodeId,
      whatsappNumber,
      countryId,
      cityId,
      birthCountryId,
      birthCityId,
      address,
      gender,
      languageId,
      comissionPlanId,
      currencyId,
      state,
      isLocked,
      referred,
      roleId,
      userType,
      localName,
      localAddress,
      localMapLink,
      localPhone,
      localWhatsappCodeId,
      localWhatsappNumber,
      localOpeningTime,
      localClosingTime,
      transactionCommission: this.normalizeTransactionCommission(transactionCommission),
    };
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  public validateKeypress(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (/^[0-9]$/.test(event.key)) {
      return;
    }

    const input = event.target as HTMLInputElement;
    if (event.key === '+' && input.selectionStart === 0 && !input.value.includes('+')) {
      return;
    }

    event.preventDefault();
  }

  public validatePaste(event: ClipboardEvent): void {
    let clipboardData = event.clipboardData?.getData('text') ?? '';
    clipboardData = clipboardData.replace(/\s+/g, '');

    const phoneNumberPattern = /^\+?[0-9]*$/;
    if (!phoneNumberPattern.test(clipboardData)) {
      event.preventDefault();
    } else {
      setTimeout(() => {
        const input = event.target as HTMLInputElement;
        const cleaned = input.value.replace(/\s+/g, '');
        input.value = cleaned;
        const controlName = input.getAttribute('formControlName');
        if (controlName) {
          this.agentModalForm?.get(controlName)?.setValue(cleaned);
        }
      });
    }
  }

  get referredControls() {
    return (this.agentModalForm?.get('referred') as FormArray).controls;
  }

  addReferred() {
    const referredGroup = this._formBuilder.group({
      referralId: [null],
      percentage: [{ value: null, disabled: false }, [Validators.min(0.5), Validators.max(100)]],
    });
    this.linkPercentageToReferral(referredGroup);

    (this.agentModalForm?.get('referred') as FormArray).push(referredGroup);
  }

  // Clearing a row's referral also clears its percentage, so the row counts as empty and
  // cleanEmptyReferred drops it. Used for both new and loaded rows; torn down with the form.
  private linkPercentageToReferral(referredGroup: FormGroup): void {
    const referralControl = referredGroup.get('referralId');
    referralControl?.valueChanges
      .pipe(takeUntil(merge(this.unsubscribe$, this.formReset$)))
      .subscribe((value) => {
        // Disabling the whole form (read-only view) re-emits values; don't re-enable on that.
        if (referralControl.disabled) return;

        const percentageControl = referredGroup.get('percentage');
        if (value) {
          percentageControl?.enable();
        } else {
          percentageControl?.disable();
          percentageControl?.reset();
        }
      });
  }

  public addReferredFromUpline(): void {
    const referred = this.agentModalForm?.get('referred') as FormArray;
    const previousId: string | null = referred.length
      ? (referred.at(referred.length - 1).get('referralId')?.value ?? null)
      : null;

    this.addReferred();
    if (!previousId) return;

    const newRow = referred.at(referred.length - 1);
    const filter = { id: previousId, type: USER_AGENT };
    this._userService
      .findUsers(filter)
      .pipe(
        switchMap((res) => {
          const agent = res.data.result?.[0];
          return agent
            ? of(agent)
            : this._userService
                .findUsers({ ...filter, isLocked: true })
                .pipe(map((locked) => locked.data.result?.[0] ?? null));
        }),
        takeUntil(merge(this.unsubscribe$, this.formReset$))
      )
      .subscribe((agent) => {
        const upline = agent?.referred?.[0]?.referralId;
        if (!upline?._id || newRow.get('referralId')?.value) return;

        const uplineId = String(upline._id);
        const alreadyListed = referred.controls.some(
          (row) => row !== newRow && row.get('referralId')?.value === uplineId
        );
        if (alreadyListed) return;

        this._ngUsers.addDataToStream([{ label: upline.username || 'N/A', value: uplineId }]);
        newRow.patchValue({ referralId: uplineId });
      });
  }

  removeReferred(index: number) {
    (this.agentModalForm?.get('referred') as FormArray).removeAt(index);
  }

  private normalizeTransactionCommission(
    value: Partial<TransactionCommission> | null | undefined
  ): TransactionCommission {
    if (!value?.enabled) {
      return { enabled: false, depositRate: 0, withdrawalRate: 0 };
    }
    return {
      enabled: true,
      depositRate: Number(value.depositRate) || 0,
      withdrawalRate: Number(value.withdrawalRate) || 0,
    };
  }

  private cleanEmptyReferred(): void {
    const referredFormArray = this.agentModalForm?.get('referred') as FormArray;
    for (let i = referredFormArray.length - 1; i >= 0; i--) {
      const referredGroup = referredFormArray.at(i) as FormGroup;
      const referralId = referredGroup.get('referralId')?.value;
      const percentage = referredGroup.get('percentage')?.value;
      if (!referralId && !percentage) {
        referredFormArray.removeAt(i);
      }
    }
  }

  private toPercentageOptionValue(percentage: number | string | null | undefined): string | null {
    return percentage || percentage === 0 ? String(percentage) : null;
  }

  percentageRequiredIfReferralSelected(control: FormGroup) {
    const referralId = control.get('referralId')?.value;
    const percentage = control.get('percentage');

    if (referralId && !percentage?.value) {
      return { percentageRequired: true };
    }
    return null;
  }

  public onRoleChange(selectedRole: NgSelect<string>): void {
    if (!selectedRole) {
      this.agentModalForm?.patchValue({ userType: null });
      this.setPhysicalCashRole(false);
      return;
    }

    const { userType, isPhysicalCash } = selectedRole;
    this.agentModalForm?.patchValue({ userType });
    this.setPhysicalCashRole(!!isPhysicalCash);
  }

  private setPhysicalCashRole(isPhysicalCash: boolean): void {
    this.isPhysicalCashRole = isPhysicalCash;
    if (!this.agentModalForm) return;
    this.physicalCashRequiredFields.forEach((field) => {
      const control = this.agentModalForm!.get(field);
      if (!control) return;

      if (isPhysicalCash) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity();
    });
  }

  private subscribeToTransactionCommissionToggle(): void {
    const transactionCommission = this.agentModalForm?.get('transactionCommission');
    if (!transactionCommission) return;

    const depositRate = transactionCommission.get('depositRate');
    const withdrawalRate = transactionCommission.get('withdrawalRate');

    transactionCommission
      .get('enabled')
      ?.valueChanges.pipe(takeUntil(merge(this.unsubscribe$, this.formReset$)))
      .subscribe((enabled) => {
        if (enabled) {
          depositRate?.enable({ emitEvent: false });
          withdrawalRate?.enable({ emitEvent: false });
        } else {
          depositRate?.disable({ emitEvent: false });
          withdrawalRate?.disable({ emitEvent: false });
        }
      });
  }

  private subscribeToWhatsappMirror(): void {
    if (!this.agentModalForm) return;

    this.agentModalForm
      .get('whatsappCodeId')
      ?.valueChanges.pipe(takeUntil(merge(this.unsubscribe$, this.formReset$)))
      .subscribe((value) => {
        const localControl = this.agentModalForm?.get('localWhatsappCodeId');
        if (localControl?.pristine) localControl.setValue(value);
      });

    this.agentModalForm
      .get('whatsappNumber')
      ?.valueChanges.pipe(takeUntil(merge(this.unsubscribe$, this.formReset$)))
      .subscribe((value) => {
        const localControl = this.agentModalForm?.get('localWhatsappNumber');
        if (localControl?.pristine) localControl.setValue(value);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
    this.formReset$.complete();
  }
}
